import { useState } from 'react'
import {
  Card, Table, Row, Col, Select, DatePicker, Statistic, Typography, Divider,
} from 'antd'
import {
  ExperimentOutlined, DollarOutlined, ThunderboltOutlined, InboxOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Text } = Typography

interface CostByCount {
  count_description: string
  production_kg: number
  rm_cost: number
  total_cost: number
}

interface CostOfProductionData {
  rm_cost_per_kg: number
  conversion_cost_per_kg: number
  total_cost_per_kg: number
  total_production_kg: number
  total_rm_cost: number
  by_count: CostByCount[]
}

interface ByVarietyRow {
  variety: string
  bales: number
  weight_kg: number
  avg_cost_per_kg: number
}

interface BySupplierRow {
  supplier: string
  bales: number
  weight_kg: number
  avg_cost_per_kg: number
}

interface RmConsumptionData {
  total_bales_consumed: number
  total_weight_kg: number
  avg_cost_per_kg: number
  by_variety: ByVarietyRow[]
  by_supplier: BySupplierRow[]
}

function fmtRs(v: number | undefined | null, decimals = 2) {
  if (v == null) return '—'
  return `₹${Number(v).toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`
}

function fmtNum(v: number | undefined | null, decimals = 3) {
  if (v == null) return '—'
  return Number(v).toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

export default function CostOfProductionPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const [period, setPeriod] = useState<dayjs.Dayjs>(dayjs())

  const year = period?.year()
  const month = period?.month() + 1

  const { data: copData, isLoading: copLoading } = useQuery<CostOfProductionData>({
    queryKey: ['cost-of-production', plantId, year, month],
    queryFn: () =>
      apiClient
        .get('/mis/cost-of-production', { params: { plant_id: plantId, year, month } })
        .then((r) => r.data),
    enabled: !!plantId,
  })

  const { data: rmData, isLoading: rmLoading } = useQuery<RmConsumptionData>({
    queryKey: ['rm-consumption', plantId, year, month],
    queryFn: () =>
      apiClient
        .get('/mis/rm-consumption', { params: { plant_id: plantId, year, month } })
        .then((r) => r.data),
    enabled: !!plantId,
  })

  const costByCountColumns = [
    {
      title: 'Count',
      dataIndex: 'count_description',
      key: 'count_description',
      ellipsis: true,
    },
    {
      title: 'Production (kg)',
      dataIndex: 'production_kg',
      key: 'production_kg',
      align: 'right' as const,
      render: (v: number) => fmtNum(v),
    },
    {
      title: 'RM Cost (₹/kg)',
      dataIndex: 'rm_cost',
      key: 'rm_cost',
      align: 'right' as const,
      render: (v: number) => fmtRs(v),
    },
    {
      title: 'Total Cost (₹/kg)',
      dataIndex: 'total_cost',
      key: 'total_cost',
      align: 'right' as const,
      render: (v: number) => (
        <Text strong style={{ color: '#cf1322' }}>
          {fmtRs(v)}
        </Text>
      ),
    },
  ]

  const byVarietyColumns = [
    {
      title: 'Variety',
      dataIndex: 'variety',
      key: 'variety',
      ellipsis: true,
    },
    {
      title: 'Bales',
      dataIndex: 'bales',
      key: 'bales',
      align: 'right' as const,
      width: 70,
    },
    {
      title: 'Weight (kg)',
      dataIndex: 'weight_kg',
      key: 'weight_kg',
      align: 'right' as const,
      width: 110,
      render: (v: number) => fmtNum(v),
    },
    {
      title: 'Avg Cost',
      dataIndex: 'avg_cost_per_kg',
      key: 'avg_cost_per_kg',
      align: 'right' as const,
      width: 100,
      render: (v: number) => fmtRs(v),
    },
  ]

  const bySupplierColumns = [
    {
      title: 'Supplier',
      dataIndex: 'supplier',
      key: 'supplier',
      ellipsis: true,
    },
    {
      title: 'Bales',
      dataIndex: 'bales',
      key: 'bales',
      align: 'right' as const,
      width: 70,
    },
    {
      title: 'Weight (kg)',
      dataIndex: 'weight_kg',
      key: 'weight_kg',
      align: 'right' as const,
      width: 110,
      render: (v: number) => fmtNum(v),
    },
    {
      title: 'Avg Cost',
      dataIndex: 'avg_cost_per_kg',
      key: 'avg_cost_per_kg',
      align: 'right' as const,
      width: 100,
      render: (v: number) => fmtRs(v),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="Cost of Production"
        subtitle="Spinning mill — RM and conversion cost analysis by count and variety"
      />

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={12} align="middle">
          <Col>
            <Select
              value={plantId}
              onChange={setPlantId}
              style={{ width: 180 }}
              placeholder="Select Plant"
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
          </Col>
          <Col>
            <DatePicker
              picker="month"
              value={period}
              onChange={(v) => v && setPeriod(v)}
              format="MMM YYYY"
              allowClear={false}
            />
          </Col>
        </Row>
      </Card>

      {/* Summary KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            size="small"
            style={{ borderTop: '3px solid #fa8c16' }}
            loading={copLoading}
          >
            <Statistic
              title={
                <span>
                  <ExperimentOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
                  RM Cost / kg
                </span>
              }
              value={copData?.rm_cost_per_kg ?? 0}
              precision={2}
              prefix="₹"
              valueStyle={{ color: '#fa8c16', fontWeight: 700 }}
              formatter={(v) =>
                Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            size="small"
            style={{ borderTop: '3px solid #1677ff' }}
            loading={copLoading}
          >
            <Statistic
              title={
                <span>
                  <ThunderboltOutlined style={{ marginRight: 6, color: '#1677ff' }} />
                  Conversion Cost / kg
                </span>
              }
              value={copData?.conversion_cost_per_kg ?? 0}
              precision={2}
              prefix="₹"
              valueStyle={{ color: '#1677ff', fontWeight: 700 }}
              formatter={(v) =>
                Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            size="small"
            style={{ borderTop: '3px solid #ff4d4f' }}
            loading={copLoading}
          >
            <Statistic
              title={
                <span>
                  <DollarOutlined style={{ marginRight: 6, color: '#ff4d4f' }} />
                  Total Cost / kg
                </span>
              }
              value={copData?.total_cost_per_kg ?? 0}
              precision={2}
              prefix="₹"
              valueStyle={{ color: '#ff4d4f', fontWeight: 700 }}
              formatter={(v) =>
                Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            size="small"
            style={{ borderTop: '3px solid #52c41a' }}
            loading={copLoading}
          >
            <Statistic
              title={
                <span>
                  <InboxOutlined style={{ marginRight: 6, color: '#52c41a' }} />
                  Production (kg)
                </span>
              }
              value={copData?.total_production_kg ?? 0}
              precision={3}
              valueStyle={{ color: '#52c41a', fontWeight: 700 }}
              formatter={(v) =>
                Number(v).toLocaleString('en-IN', { minimumFractionDigits: 3 })
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Detail Sections */}
      <Row gutter={[16, 16]}>
        {/* Left: Cost by Count */}
        <Col xs={24} md={12}>
          <Card
            title="Cost by Count"
            size="small"
            loading={copLoading}
          >
            <Table
              dataSource={(copData?.by_count ?? []).map((r, i) => ({ ...r, key: i }))}
              columns={costByCountColumns}
              size="small"
              pagination={false}
              scroll={{ x: 480 }}
              summary={() => {
                const byCount = copData?.by_count ?? []
                if (byCount.length === 0) return null
                const totalProd = byCount.reduce((s, r) => s + r.production_kg, 0)
                return (
                  <Table.Summary.Row style={{ background: '#f0f5ff' }}>
                    <Table.Summary.Cell index={0}>
                      <Text strong>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong>{fmtNum(totalProd)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong>{fmtRs(copData?.rm_cost_per_kg)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <Text strong style={{ color: '#cf1322' }}>
                        {fmtRs(copData?.total_cost_per_kg)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )
              }}
            />
          </Card>
        </Col>

        {/* Right: By Variety + By Supplier stacked */}
        <Col xs={24} md={12}>
          <Card
            title="RM Consumption — By Variety"
            size="small"
            loading={rmLoading}
            style={{ marginBottom: 16 }}
          >
            <Table
              dataSource={(rmData?.by_variety ?? []).map((r, i) => ({ ...r, key: i }))}
              columns={byVarietyColumns}
              size="small"
              pagination={false}
              scroll={{ x: 400 }}
              summary={() => {
                if (!rmData || (rmData.by_variety ?? []).length === 0) return null
                return (
                  <Table.Summary.Row style={{ background: '#f6ffed' }}>
                    <Table.Summary.Cell index={0}>
                      <Text strong>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong>{rmData.total_bales_consumed}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong>{fmtNum(rmData.total_weight_kg)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <Text strong>{fmtRs(rmData.avg_cost_per_kg)}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )
              }}
            />
          </Card>

          <Card
            title="RM Consumption — By Supplier"
            size="small"
            loading={rmLoading}
          >
            <Table
              dataSource={(rmData?.by_supplier ?? []).map((r, i) => ({ ...r, key: i }))}
              columns={bySupplierColumns}
              size="small"
              pagination={false}
              scroll={{ x: 400 }}
              summary={() => {
                if (!rmData || (rmData.by_supplier ?? []).length === 0) return null
                return (
                  <Table.Summary.Row style={{ background: '#f6ffed' }}>
                    <Table.Summary.Cell index={0}>
                      <Text strong>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong>{rmData.total_bales_consumed}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong>{fmtNum(rmData.total_weight_kg)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <Text strong>{fmtRs(rmData.avg_cost_per_kg)}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* RM Cost footer note */}
      {copData && (
        <Card size="small" style={{ marginTop: 16, background: '#fffbe6', borderColor: '#ffe58f' }}>
          <Row gutter={32}>
            <Col>
              <Text type="secondary">Total RM Cost: </Text>
              <Text strong style={{ color: '#fa8c16' }}>{fmtRs(copData.total_rm_cost)}</Text>
            </Col>
            <Col>
              <Text type="secondary">Total Bales Consumed: </Text>
              <Text strong>{rmData?.total_bales_consumed ?? '—'}</Text>
            </Col>
            <Col>
              <Text type="secondary">Total RM Weight: </Text>
              <Text strong>{rmData ? fmtNum(rmData.total_weight_kg) + ' kg' : '—'}</Text>
            </Col>
            <Divider type="vertical" />
            <Col>
              <Text type="secondary">Avg RM Cost/kg: </Text>
              <Text strong style={{ color: '#fa8c16' }}>
                {rmData ? fmtRs(rmData.avg_cost_per_kg) : '—'}
              </Text>
            </Col>
          </Row>
        </Card>
      )}
    </div>
  )
}
